/**
 * DOM Manipulation Utilities
 */

export class DOMUtils {
  /**
   * Safely query selector
   */
  static querySelector<T extends HTMLElement>(selector: string): T | null {
    return document.querySelector<T>(selector);
  }

  /**
   * Safely query selector all
   */
  static querySelectorAll<T extends HTMLElement>(selector: string): NodeListOf<T> {
    return document.querySelectorAll<T>(selector);
  }

  /**
   * Add event listener with cleanup
   */
  static addEventListener<K extends keyof HTMLElementEventMap>(
    element: HTMLElement,
    event: K,
    handler: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions
  ): () => void {
    element.addEventListener(event, handler, options);
    return () => element.removeEventListener(event, handler, options);
  }

  /**
   * Toggle class on element
   */
  static toggleClass(element: HTMLElement, className: string, force?: boolean): void {
    element.classList.toggle(className, force);
  }

  /**
   * Check if element has class
   */
  static hasClass(element: HTMLElement, className: string): boolean {
    return element.classList.contains(className);
  }

  /**
   * Add class to element
   */
  static addClass(element: HTMLElement, ...classNames: string[]): void {
    element.classList.add(...classNames);
  }

  /**
   * Remove class from element
   */
  static removeClass(element: HTMLElement, ...classNames: string[]): void {
    element.classList.remove(...classNames);
  }

  /**
   * Show element
   */
  static show(element: HTMLElement, display: string = 'block'): void {
    element.style.display = display;
  }

  /**
   * Hide element
   */
  static hide(element: HTMLElement): void {
    element.style.display = 'none';
  }

  /**
   * Create element with attributes
   */
  static createElement<K extends keyof HTMLElementTagNameMap>(
    tagName: K,
    attributes?: Record<string, string>,
    content?: string
  ): HTMLElementTagNameMap[K] {
    const element = document.createElement(tagName);

    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        element.setAttribute(key, value);
      });
    }

    if (content) {
      element.textContent = content;
    }

    return element;
  }

  /**
   * Insert HTML safely
   */
  static setInnerHTML(element: HTMLElement, html: string): void {
    element.innerHTML = html;
  }

  /**
   * Insert adjacent HTML
   */
  static insertAdjacentHTML(element: HTMLElement, position: InsertPosition, html: string): void {
    element.insertAdjacentHTML(position, html);
  }

  /**
   * Get elements by class name
   */
  static getElementsByClassName(className: string, parent: HTMLElement | Document = document): HTMLCollectionOf<Element> {
    return parent.getElementsByClassName(className);
  }

  /**
   * Wait for element to be available in DOM
   */
  static waitForElement<T extends HTMLElement>(
    selector: string,
    timeout: number = 5000
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const element = document.querySelector<T>(selector);
      if (element) {
        resolve(element);
        return;
      }

      const observer = new MutationObserver(() => {
        const element = document.querySelector<T>(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);
    });
  }
}

